import React, { useState } from 'react';
import CertificateInformation from '../check-certificates/CertificateInformation';
import SmallSpinner from '../../components/spinner/SmallSpinner';
import notifyHB from '../../config/notifyHB';
import { toast } from '@/components/toast/toast';
import { rawHttp } from '../../http';
import endpointConfig from '@/config/endpointConfig';
import { Button } from '@/components/ui/button';
import styles from './document-information.module.scss';
import { ArrowLeft, CheckCheck, CircleX, Loader2Icon } from 'lucide-react';

type DocumentInformationProps = {
	returnStep: () => void;
	documentName: string;
	signatureDate: string;
	signerCredentials: string;
	base64Hash: string;
	signedCyberSign: boolean;
};

const statuses: Record<string, string> = {
	'0': 'unspecified',
	'1': 'Key Compromise',
	'2': 'cA Compromise',
	'3': 'Affiliation Changed',
	'4': 'Superseded',
	'5': 'Cessation Of Operation',
	'6': 'Suspended',
	'8': 'Remove From CRL',
	'9': 'privilegeWithdrawn',
	'10': 'aACompromise',
};

const DocumentInformation: React.FC<DocumentInformationProps> = ({
	returnStep,
	documentName,
	signatureDate,
	signerCredentials,
	base64Hash,
	signedCyberSign,
}) => {
	type CheckResponse = {
		caSerial: string;
		certType: string;
		certificateStatus: string;
		createdAt: string;
		expirationDate: string;
		emailDeclared: string;
		serial: string;
		revocationTimeStamp: string;
		suscriberName: string;
	};
	const [certInformation, setCertInformation] = useState(<div></div>);
	const [displayButton, setDisplayButton] = useState(true);
	const [setLoading, setSetLoading] = useState(false);

	const returnCertificateStatus = (certStatus: string) => {
		if (statuses.hasOwnProperty(certStatus)) {
			return statuses[certStatus];
		} else {
			return certStatus;
		}
	};

	const getCertInformation = async () => {
		setSetLoading(true);

		try {
			const certcheckResponse = await rawHttp.post(
				endpointConfig.certcheck,
				{
					publicKeyHashHex: base64Hash,
					certificate_serial: signerCredentials,
				},
			);

			const certificateInformation: CheckResponse = JSON.parse(
				certcheckResponse.data.certificateInformation,
			);

			setCertInformation(
				<CertificateInformation
					caSerial={certificateInformation['caSerial']}
					certType={certificateInformation['certType']}
					certificateStatus={returnCertificateStatus(
						certificateInformation['certificateStatus'],
					)}
					createdAt={certificateInformation['createdAt']}
					expirationDate={certificateInformation['expirationDate']}
					emailDeclared={certificateInformation['emailDeclared']}
					serial={certificateInformation['serial']}
					revocationTimeStamp={
						certificateInformation['revocationTimeStamp']
					}
					suscriberName={certificateInformation['suscriberName']}
				/>,
			);
			setSetLoading(false);
			setDisplayButton(false);
		} catch (err) {
			toast({ mode: 'destructive', description: 'Hubo un error en la validación del documento' });
			notifyHB('verify-document', 'DocumentInformation', err);
			returnStep();
		}
	};

	return (
		<div>
			<div className="cyber-spacer medium"></div>

			<div className={styles['document-information']}>
				<div className={styles['title']}>
					<Button variant="ghost" onClick={returnStep}>
						<ArrowLeft />
					</Button>
					<div className={styles['text']}>{documentName}</div>
				</div>

				{signedCyberSign ? (
					<div className={styles['content']}>
						<div className={styles['field-name']}>
							Fecha de firma:
						</div>
						<div className={styles['field-value']}>
							{signatureDate}
						</div>
						<div className={styles['field-name']}>
							Número de serie del certificado firmante:
						</div>
						<div className={styles['field-value']}>
							{signerCredentials}
						</div>
						<div className={styles['field-name']}>
							Certificado registrado:
						</div>
						<div className={styles['field-value']}>
							{signerCredentials !== '' ? (
								<CheckCheck />
							) : (
								<SmallSpinner />
							)}
						</div>
					</div>
				) : (
					<div className={styles['not-signed']}>
						<CircleX size={40} />
						<p>El documento no fue firmado por Cybersign</p>
					</div>
				)}
				{displayButton ? (
					<div>
						<Button variant="outline" onClick={getCertInformation}>
							{setLoading && (
								<Loader2Icon className="animate-spin" />
							)}
							Más información
						</Button>
					</div>
				) : (
					certInformation
				)}
			</div>
		</div>
	);
};

export default DocumentInformation;
